/**
 * An on-screen diagnostics panel, for reading a scan on a phone with no debugger attached (KAN-3, D-62).
 *
 * Every hard finding in this integration came from a phone's console read over a cable (D-27, D-38,
 * D-54), and that needs a Mac for an iPhone. This puts the few numbers that matter on the page itself,
 * and - because iOS answers memory pressure by killing the tab and reloading it - writes the latest
 * snapshot to localStorage twice a second, so the page that comes back can say what the one that died
 * was doing and how far in it got.
 *
 * Off unless /config.json says `"diagnostics": true`, which no deployed installation does. Strings only:
 * nothing here holds a reference to a vendor object (D-38).
 */

const STORAGE_KEY = 'faceheart:diagnostics:last-run'
const PERSIST_EVERY_MS = 500

interface Snapshot {
  readonly startedAt: number
  readonly updatedAt: number
  readonly values: Record<string, string>
}

let enabled = false
let panel: HTMLPreElement | null = null
let previous: Snapshot | null = null
let startedAt = Date.now()
const values: Record<string, string> = {}
let lastPersisted = 0
/** The patient's tap and the camera answering it - kept across `diagnosticsRunStarted`, which comes after both. */
let tappedAt: number | null = null
let cameraOpenedAt: number | null = null
let cameraDescription = ''
let renderQueued = false

export function enableDiagnostics(): void {
  if (enabled) return
  enabled = true
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    previous = stored ? (JSON.parse(stored) as Snapshot) : null
  } catch {
    previous = null
  }
  panel = document.createElement('pre')
  panel.setAttribute('data-testid', 'diagnostics')
  Object.assign(panel.style, {
    position: 'fixed',
    left: '4px',
    right: '4px',
    bottom: '4px',
    zIndex: '2147483647',
    margin: '0',
    padding: '6px',
    font: '10px/1.3 ui-monospace, monospace',
    color: '#0f0',
    background: 'rgba(0,0,0,0.75)',
    borderRadius: '6px',
    whiteSpace: 'pre-wrap',
    pointerEvents: 'none',
    maxHeight: '45vh',
    overflow: 'hidden',
  })
  document.body.appendChild(panel)
  captureWarnings()
  render()
  // The clock line moves even when nothing else does - which is exactly when it matters.
  setInterval(render, 1_000)
}

/**
 * The patient's tap on "start scan", and the camera answering it. In headless Chromium a camera opened
 * while both runtimes were still warming took 12 s to answer, against ~1 s without the warm-up (D-63); the
 * run line says how long a phone took, which is the number that decides whether the warm-up is worth it.
 */
export function diagnosticsTapped(): void {
  tappedAt = Date.now()
  cameraOpenedAt = null
  cameraDescription = ''
}

/** The camera answered - and what it actually delivers, which on Android is not always what was asked for. */
export function diagnosticsCameraOpened(stream?: MediaStream | null): void {
  cameraOpenedAt = Date.now()
  cameraDescription = describeCamera(stream)
}

/** `640x480@30fps`, from the track's own settings; empty when there is no track or it says nothing. */
export function describeCamera(stream?: MediaStream | null): string {
  try {
    const settings = stream?.getVideoTracks()[0]?.getSettings()
    if (!settings) return ''
    const size = settings.width && settings.height ? `${settings.width}x${settings.height}` : '?x?'
    const rate = settings.frameRate ? `@${Math.round(settings.frameRate)}fps` : ''
    return size + rate
  } catch {
    return ''
  }
}

/** Starts a new run: the clock the panel's times are measured from, and a clean slate. */
export function diagnosticsRunStarted(label: string): void {
  if (!enabled) return
  startedAt = Date.now()
  for (const key of Object.keys(values)) delete values[key]
  for (const key of Object.keys(lines)) delete lines[key]
  const sinceTap =
    tappedAt === null
      ? ''
      : ` (tap -> camera ${cameraOpenedAt === null ? '?' : seconds(cameraOpenedAt - tappedAt) + 's'}${cameraDescription ? ' ' + cameraDescription : ''}, tap -> run ${seconds(startedAt - tappedAt)}s)`
  diag('run', label + sinceTap)
}

/** Sets one line of the panel. Objects are flattened to `key=value` pairs. */
export function diag(key: string, value: unknown): void {
  if (!enabled) return
  values[key] = format(value)
  queueRender()
  const now = Date.now()
  if (now - lastPersisted >= PERSIST_EVERY_MS) {
    lastPersisted = now
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ startedAt, updatedAt: now, values } satisfies Snapshot))
    } catch {
      // Storage refused (private mode, quota): the panel still shows the live run.
    }
  }
}

/**
 * The page's last few warnings and errors, as STRINGS, on the panel: on an iPhone with no Web Inspector
 * they are otherwise invisible, and "Mindset never got going" has a dozen causes only its own console
 * names. Truncated and capped, and nothing retained but the text (D-38).
 */
const WARNINGS_KEPT = 6
const warnings: string[] = []

function captureWarnings(): void {
  const keep = (level: string, args: readonly unknown[]) => {
    const text = args.map((arg) => (arg instanceof Error ? `${arg.name}: ${arg.message}` : typeof arg === 'object' ? safeJson(arg) : String(arg))).join(' ')
    warnings.push(`${seconds(Date.now() - startedAt)}s ${level} ${text}`.slice(0, 180))
    if (warnings.length > WARNINGS_KEPT) warnings.shift()
    diag('warnings', `\n    ${warnings.join('\n    ')}`)
  }
  for (const level of ['warn', 'error'] as const) {
    const original = console[level].bind(console)
    console[level] = (...args: unknown[]) => {
      original(...args)
      keep(level, args)
    }
  }
  window.addEventListener('error', (event) => keep('uncaught', [event.message]))
  window.addEventListener('unhandledrejection', (event) => keep('unhandled', [event.reason]))
}

/**
 * A rolling list of the last few lines from one source, as its own panel entry - for a vendor whose own
 * log lives in a Web Worker, where `console` wrapping on this page never sees it (Mindset's
 * `loadWasmModule` failures, its WASM environment report).
 */
const LINES_KEPT = 8
const lines: Record<string, string[]> = {}

export function diagLine(key: string, text: string): void {
  if (!enabled) return
  const list = (lines[key] ??= [])
  list.push(`${seconds(Date.now() - startedAt)}s ${text}`.slice(0, 200))
  if (list.length > LINES_KEPT) list.shift()
  diag(key, `\n    ${list.join('\n    ')}`)
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value)?.slice(0, 120) ?? String(value)
  } catch {
    return String(value)
  }
}

function format(value: unknown): string {
  if (value === null || value === undefined) return String(value)
  if (typeof value !== 'object') return String(value)
  return Object.entries(value as Record<string, unknown>)
    .map(([key, inner]) => `${key}=${typeof inner === 'object' && inner !== null ? JSON.stringify(inner) : String(inner)}`)
    .join(' ')
}

// A timer rather than requestAnimationFrame: a page that is not being painted (a backgrounded tab, an
// occluded window) runs no animation frames, and a panel that stops updating looks like a scan that stopped.
function queueRender(): void {
  if (renderQueued) return
  renderQueued = true
  setTimeout(() => {
    renderQueued = false
    render()
  }, 100)
}

function render(): void {
  if (!panel) return
  const lines = [`now  +${seconds(Date.now() - startedAt)}s`]
  for (const [key, value] of Object.entries(values)) lines.push(`${key}: ${value}`)
  if (previous) {
    lines.push('', `PREVIOUS RUN (last written +${seconds(previous.updatedAt - previous.startedAt)}s, ${new Date(previous.updatedAt).toLocaleTimeString()}):`)
    for (const [key, value] of Object.entries(previous.values)) lines.push(`  ${key}: ${value}`)
  }
  panel.textContent = lines.join('\n')
}

function seconds(ms: number): string {
  return (ms / 1000).toFixed(1)
}
